import React from 'react'
import { CaseStudyCard } from '../components/caseStudy/CaseStudyCard'
import { SEOHead } from '../components/seo/SEOHead'
import { Card, Container, Heading, Text } from '../components/ui'
import { caseStudies } from '../data/caseStudies'
import '../styles/CaseStudiesPage.css'

/**
 * Case Studies page component showcasing technical case studies
 * demonstrating software architecture expertise and business impact.
 *
 * Features:
 * - Clean presentation of case studies
 * - Professional layout with responsive design
 * - SEO optimized for technical content discovery
 * - Accessible navigation and interactions
 */
const CaseStudiesPage: React.FC = () => {
  return (
    <>
      {/* SEO Meta Tags */}
      <SEOHead page="case-studies" path="/case-studies" />

      <main className="page-main" role="main">
        <Container size="lg" padding="lg">
          {/* Page Header */}
          <header className="case-studies-header">
            <Heading as="h1" size="3xl" variant="gradient">
              Case Studies
            </Heading>
            <Text
              size="lg"
              variant="secondary"
              className="case-studies-subtitle"
            >
              Deep technical analysis of software architecture challenges and
              solutions
            </Text>
          </header>

          {/* Case Studies Grid */}
          <section
            className="case-studies-grid"
            role="region"
            aria-label={`${caseStudies.length} case studies`}
          >
            {caseStudies.map(caseStudy => (
              <CaseStudyCard
                key={caseStudy.id}
                caseStudy={caseStudy}
                featured={caseStudy.featured}
              />
            ))}
          </section>

          {/* Call to Action */}
          <section
            className="case-studies-cta"
            role="region"
            aria-label="Additional information"
          >
            <Card variant="glass" padding="lg" className="cta-card">
              <div className="cta-content">
                <Heading as="h2" size="lg">
                  Ready to Discuss Your Next Project?
                </Heading>
                <Text size="base" variant="secondary">
                  These case studies represent examples of the challenges I've
                  solved. Let's discuss how I can help architect solutions for
                  your specific needs.
                </Text>
                <div className="cta-actions">
                  <a
                    href="mailto:lucas@lucasstufflebeam.com"
                    className="button button--primary button--lg"
                    aria-label="Contact Lucas Stufflebeam via email"
                  >
                    Start a Conversation
                  </a>
                  <a
                    href="/resume"
                    className="button button--outline button--lg"
                    aria-label="View Lucas Stufflebeam's resume"
                  >
                    View Resume
                  </a>
                </div>
              </div>
            </Card>
          </section>
        </Container>
      </main>
    </>
  )
}

export default CaseStudiesPage
